// One shared way for a workspace list card to report a MANUALLY changed filter.
//
// Every list in the workspace (tasks, meetings, properties, …) keeps its filter in local state and
// queries REST directly, so a filter the user clicks never passes through an agent turn — which is
// why manual filters used to be invisible to the "⋯" recent-views menu. Rather than each list
// growing its own reporting effect (with its own subtly different skip/dedupe rules), they all
// call this hook and the behaviour lives in one place.
//
// It deliberately skips the FIRST set of params: those came from the agent's filter via the
// `initial*` props and are already recorded — reporting them again would just re-stamp the entry
// the agent had already created.
import { useEffect, useMemo, useRef } from 'react';
import { normalizeViewParams } from '@/components/chat/recentViews';

/** Called with the list's effective filter whenever the USER changes it. */
export type OnWorkspaceFilterChange = (params: Record<string, unknown>) => void;

export function useWorkspaceFilterReport(
  params: Record<string, unknown>,
  onFiltersChange?: OnWorkspaceFilterChange,
): void {
  // Canonical form of the current params — also the effect's dependency, so a re-render that
  // rebuilds an equivalent params object doesn't re-run the effect at all.
  const normalized = useMemo(() => normalizeViewParams(params), [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const key = useMemo(() => JSON.stringify(normalized), [normalized]);
  // The last key we reported (or the mount-time baseline).
  const lastRef = useRef<string | null>(null);
  // Held in a ref so an inline arrow prop doesn't re-trigger the effect on every parent render.
  const handlerRef = useRef(onFiltersChange);
  handlerRef.current = onFiltersChange;

  useEffect(() => {
    if (lastRef.current === null) {
      lastRef.current = key; // baseline: the agent-supplied filter this card opened with
      return;
    }
    if (lastRef.current === key) return;
    lastRef.current = key;
    handlerRef.current?.(normalized);
  }, [key, normalized]);
}
